import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { Icon } from 'uxd-design-system-components';
import './HopesInfo.scss';
import StyledModal from '../../components/StyledModal/StyledModal';
import {
  filterRetirementOptions,
  filterRetirementAnswers,
} from '../../utils/filterRetirement/filterRetirement';
import {
  AnswersContext,
  LocaleContext,
  QueryContext,
  PreviousPathContext,
} from '../../contexts';
import getText from '../../assets/locale/locale';
import addDataLayerTrigger from '../../utils/addDataLayerTrigger/addDataLayerTrigger';
import CheckList from '../../components/CheckList/CheckList';
import getStatusForChecklist from '../../utils/getStatusForChecklist/getStatusForChecklist';
import ACTIONS from '../../constants/actions';
import getLabels from '../../utils/getLabels/getLabels';

const HopesInfo = ({ setCurrentStep, schema, faInfo, sessionId }) => {
  const [showModal, setShowModal] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const [localHopes, setLocalHopes] = useState([]);
  const [localWorries, setLocalWorries] = useState([]);
  const [localHopesArr, setLocalHopesArr] = useState([]);
  const [localWorriesArr, setLocalWorriesArr] = useState([]);
  const [hopesSchema, setHopesSchema] = useState([]);
  const [worriesSchema, setWorriesSchema] = useState([]);
  const locId = useContext(LocaleContext);
  const { previousPath, setPreviousPath } = useContext(PreviousPathContext);

  const { answers, answersDispatch } = useContext(AnswersContext);
  const { queryDispatch } = useContext(QueryContext);
  const { hope, worry } = schema;

  const employmentStatusRetired = getText(
    locId,
    'HopesInfo',
    'employmentStatusRetired'
  );

  const topOfHopesPage = useRef();

  const removeFocus = () => {
    const heading = document.getElementById('heading-no-focus');
    if (heading !== null) {
      heading.removeAttribute('tabIndex');
    }
  };

  useEffect(() => {
    document.title = getText(locId, 'HopesInfo', 'pageTitle');
    addDataLayerTrigger('dataLayer-initialized', {
      page_type: 'hopesPage',
      locale: locId,
      environment: window.ENVIRONMENT,
      sessionid: sessionId ?? undefined,
      fa_no: faInfo.faNo ?? undefined,
      fa_entity_id: faInfo.entityIdBase64 ?? undefined,
    });
    addDataLayerTrigger('VirtualPageview', {
      page_path: window.location.pathname,
      page_title: document.title,
      page_referrer: previousPath,
      external_referrer:
        window.document.referrer && window.document.referrer.trim() !== ''
          ? window.document.referrer
          : undefined,
      page_url: window.location.href,
    });

    // sort schema to remove retired options
    if (answers.statusAnswer === employmentStatusRetired) {
      if (hope) {
        setHopesSchema(hope);
        setLocalHopesArr(filterRetirementOptions(hope));
      }
      if (worry) {
        setWorriesSchema(worry);
        setLocalWorriesArr(filterRetirementOptions(worry));
      }
    } else {
      // filter labels to display to user, but display all options from the schema
      if (hope) {
        setHopesSchema(hope);
        const labels = getLabels(hope);
        setLocalHopesArr(labels);
      }
      if (worry) {
        setWorriesSchema(worry);
        const labels = getLabels(worry);
        setLocalWorriesArr(labels);
      }
    }
  }, []);

  useEffect(() => {
    if (answers.statusAnswer === employmentStatusRetired) {
      if (answers.hopeAnswer) {
        setLocalHopes(filterRetirementAnswers(answers.hopeAnswer));
      }
      if (answers.worryAnswer) {
        setLocalWorries(filterRetirementAnswers(answers.worryAnswer));
      }
    } else {
      if (answers.hopeAnswer) {
        setLocalHopes(answers.hopeAnswer);
      }
      if (answers.worryAnswer) {
        setLocalWorries(answers.worryAnswer);
      }
    }
  }, []);

  useEffect(() => {
    setCurrentStep(window.location.pathname);
    topOfHopesPage.current.focus();
    setTimeout(removeFocus, 1000);
  }, []);

  const goBack = (e) => {
    setPreviousPath(window.location.pathName);
    e.preventDefault();
    addDataLayerTrigger('go-back-to-household', {
      finalHopes: localHopes,
      finalConcerns: localWorries,
    });
    history.push({
      pathname: getText(locId, 'AllPages', 'householdPath', 'App'),
      search: location.search,
    });
  };

  const getResults = (e) => {
    setPreviousPath(window.location.pathname);
    e.preventDefault();
    addDataLayerTrigger('get-results-click', {
      finalHopes: localHopes,
      finalConcerns: localWorries,
    });
    history.push({
      pathname: getText(locId, 'AllPages', 'resultsPath', 'App'),
      search: location.search,
    });
  };

  const worryHandler = (e) => {
    const tempWorries = localWorries;
    if (tempWorries.indexOf(e) > -1) {
      tempWorries.splice(tempWorries.indexOf(e), 1);
    } else {
      tempWorries.push(e);
    }
    addDataLayerTrigger('concerns-selection', {
      concerns: tempWorries,
    });
    answersDispatch({
      type: ACTIONS.SET_GOALS,
      payload: {
        hope: localHopes,
        worry: tempWorries,
      },
    });
    queryDispatch({
      type: ACTIONS.SET_GOALS,
      payload: {
        hope: getStatusForChecklist(localHopes, hopesSchema),
        worry: getStatusForChecklist(tempWorries, worriesSchema),
      },
    });
  };

  const hopeHandler = (e) => {
    const tempHopes = localHopes;
    if (tempHopes.indexOf(e) > -1) {
      tempHopes.splice(tempHopes.indexOf(e), 1);
    } else {
      tempHopes.push(e);
    }
    addDataLayerTrigger('hopes-selection', {
      hopes: tempHopes,
    });
    answersDispatch({
      type: ACTIONS.SET_GOALS,
      payload: {
        hope: tempHopes,
        worry: localWorries,
      },
    });
    queryDispatch({
      type: ACTIONS.SET_GOALS,
      payload: {
        hope: getStatusForChecklist(tempHopes, hopesSchema),
        worry: getStatusForChecklist(localWorries, worriesSchema),
      },
    });
  };

  return (
    <main className="form-view form-style">
      <div className="form-card">
        <fieldset className="field-set accessibility">
          <legend>
            <span className="accessibility">
              {getText(locId, 'HopesInfo', 'accessibilityFieldset')}
            </span>
          </legend>
          <div className="singlescreen">
            <div className="title">
              <h1 id="heading-no-focus" ref={topOfHopesPage} tabIndex="-1">
                {getText(locId, 'HopesInfo', 'heading')}
              </h1>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
              <div
                role="button"
                className="why-ask-button"
                onClick={() => {
                  setShowModal(true);
                  addDataLayerTrigger('hopes-fears-why-ask-button');
                }}
                onKeyDown={(event) => {
                  if (event.keyCode === 13 || event.keyCode === 32) {
                    setShowModal(true);
                    addDataLayerTrigger('hopes-fears-why-ask-button');
                  }
                }}
                tabIndex="0"
              >
                <Icon name="help-question-mark" />
                {getText(locId, 'HopesInfo', 'modalHeader')}
              </div>
              <StyledModal
                headerText={getText(locId, 'HopesInfo', 'modalHeader')}
                paragraphText={getText(locId, 'HopesInfo', 'modalParagraph')}
                buttonText={getText(locId, 'HopesInfo', 'modalButton')}
                displayModal={showModal}
                closeModalHandler={() => setShowModal(false)}
              />
            </div>
            <div
              className="checklist-container"
              data-testid="checklist-container"
            >
              <CheckList
                label={getText(locId, 'HopesInfo', 'hopesLabel')}
                labelNote={getText(locId, 'HopesInfo', 'checkAllNote')}
                listItems={localHopesArr}
                id="hopes"
                onChangeHandler={hopeHandler}
                checkedItems={localHopes}
                labelId="hopes"
              />
            </div>
            <div
              className="checklist-container"
              data-testid="checklist-container"
            >
              <CheckList
                label={getText(locId, 'HopesInfo', 'worriesLabel')}
                labelNote={getText(locId, 'HopesInfo', 'checkAllNote')}
                listItems={localWorriesArr}
                onChangeHandler={worryHandler}
                checkedItems={localWorries}
                labelId="worry"
              />
            </div>
            <div className="hopes actions">
              <button
                id="hopes-next-button"
                type="submit"
                className="button button-super get-results-button"
                onClick={getResults}
              >
                {getText(locId, 'HopesInfo', 'getResults')}
              </button>
              <button
                id="hopes-back-button"
                type="button"
                className="button button-secondary"
                onClick={goBack}
              >
                {getText(locId, 'AllPages', 'backButton')}
              </button>
            </div>
          </div>
        </fieldset>
      </div>
    </main>
  );
};

HopesInfo.propTypes = {
  setCurrentStep: PropTypes.func.isRequired,
  schema: PropTypes.shape({
    dependents: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    employment_status: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    hope: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    marital_status: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
    worry: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
  }).isRequired,
  faInfo: PropTypes.objectOf(PropTypes.string),
  sessionId: PropTypes.string,
};

HopesInfo.defaultProps = {
  faInfo: {
    faFirstName: null,
    faLastName: null,
    branchNo: null,
    faNo: null,
    faPhoto: null,
  },
  sessionId: '',
};

export default HopesInfo;
